import type { ReactNode } from "react";

import { Card } from "../components/Display";
import { Input, Textarea } from "../components/Input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/Overlays";
import { cn } from "../lib/cn";
import { FormField } from "./Forms";
import { RichTextEditor, type RichTextEditorLabels } from "./RichTextEditor";

/**
 * Translations of a record's texts (P6-13): one tab per language, one input per translatable field. Empty fields mean
 * "use the English text". Controlled: `value` is `{hi: {title: "…"}, mr: {…}}`.
 */

export type Translations = Record<string, Record<string, string>>;

export interface TranslatableFieldDef {
  name: string;
  label: string;
  kind?: "text" | "textarea" | "html";
  maxLength?: number;
}

export function TranslationsEditor({
  title,
  description,
  languages,
  fields,
  value,
  onChange,
  richTextLabels,
  errors = {},
  disabled,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  languages: { code: string; label: string }[];
  fields: TranslatableFieldDef[];
  value: Translations | null | undefined;
  onChange: (value: Translations) => void;
  richTextLabels: RichTextEditorLabels;
  /** Server errors by path, e.g. {"translations.hi.title": "At most 200 characters."} */
  errors?: Record<string, string>;
  disabled?: boolean;
  className?: string;
}) {
  const current = value ?? {};
  const set = (language: string, field: string, text: string) => {
    const texts = { ...(current[language] ?? {}), [field]: text };
    if (!text) delete texts[field];
    const next = { ...current, [language]: texts };
    if (Object.keys(texts).length === 0) delete next[language];
    onChange(next);
  };
  return (
    <Card className={cn("space-y-4 p-5 sm:p-6", className)}>
      <div className="space-y-1">
        <h2 className="text-base font-semibold">{title}</h2>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      <Tabs defaultValue={languages[0]?.code}>
        <TabsList>
          {languages.map((language) => (
            <TabsTrigger key={language.code} value={language.code}>
              {language.label}
              {Object.keys(current[language.code] ?? {}).length > 0 && (
                <span className="ml-1.5 size-1.5 rounded-full bg-success" aria-hidden />
              )}
            </TabsTrigger>
          ))}
        </TabsList>
        {languages.map((language) => (
          <TabsContent key={language.code} value={language.code} className="space-y-4 pt-4">
            {fields.map((field) => {
              const text = current[language.code]?.[field.name] ?? "";
              const label = `${language.label}: ${field.label}`;
              const error = errors[`translations.${language.code}.${field.name}`];
              return (
                <FormField key={field.name} label={label} error={error}>
                  {field.kind === "html" ? (
                    <RichTextEditor
                      aria-label={label}
                      value={text}
                      onChange={(html) => set(language.code, field.name, html)}
                      labels={richTextLabels}
                      disabled={disabled}
                      invalid={Boolean(error)}
                    />
                  ) : field.kind === "textarea" ? (
                    <Textarea
                      value={text}
                      rows={3}
                      maxLength={field.maxLength}
                      disabled={disabled}
                      lang={language.code}
                      onChange={(event) => set(language.code, field.name, event.target.value)}
                    />
                  ) : (
                    <Input
                      value={text}
                      maxLength={field.maxLength}
                      disabled={disabled}
                      lang={language.code}
                      onChange={(event) => set(language.code, field.name, event.target.value)}
                    />
                  )}
                </FormField>
              );
            })}
          </TabsContent>
        ))}
      </Tabs>
    </Card>
  );
}
